import {
    Avatar,
    Box,
    Breadcrumbs,
    Button,
    Card,
    CardActions,
    Divider,
    Grid,
    Link, ListItem,
    ListItemAvatar, ListItemIcon, ListItemText,
    Paper,
    Theme,
    Typography,
    Accordion, AccordionSummary, AccordionDetails
} from '@mui/material'

import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import PinDropIcon from '@mui/icons-material/PinDrop';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MovieIcon from '@mui/icons-material/Movie';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PriceCheckIcon from '@mui/icons-material/PriceCheck';
import Grid3x3Icon from '@mui/icons-material/Grid3x3';
import LinkIcon from '@mui/icons-material/Link';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import WorkspacesIcon from '@mui/icons-material/Workspaces';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import CardMembershipIcon from '@mui/icons-material/CardMembership';
import {makeStyles} from '@mui/styles';
import {Link as RouterLink} from 'react-router-dom';
import {BLOCKCHAIN_NETWORK_ID, CURRENCY} from '@shared/constants/app.constant';

import {IToken} from 'src/interfaces/token';
import {AppSlider} from './Slider';
import {TokenOverviewSkeleton} from '@shared/skeletons/TokenPreviewSkeleton';
import {
    currencyConversion,
    formatPrice,
    getCurrencySymbol,
    getOtherCurrency,
    parseJSON
} from '@shared/helpers/global.helper';
import {useTheme} from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {useAppSelector} from '@store/hooks';
import {appSelector} from '@store/slices/app.slice';
import CardContent from "@mui/material/CardContent";
import List from "@mui/material/List";
import LogoPlaceholder from '@assets/images/BusinessLogoPlaceholder.svg'


const useStyles = makeStyles((theme: Theme) => ({
    tokenImage: {
        marginBottom: '24px'
    },
    button: {
        padding: '12px 24px !important',
        borderRadius: '8px !important'
    },
    buttonLabel: {
        fontSize: '14px !important',
        lineHeight: '16px !important',
        fontStyle: 'normal !important',
        letterSpacing: '0.05em !important'
    },
    title2: {
        fontSize: '24px !important',
        lineHeight: '28px !important',
        fontStyle: 'normal',
        marginBotton: '8px'
    },
    title3: {
        fontSize: '20px !important',
        lineHeight: '24px !important',
        fontStyle: 'normal',
        color: '#1E293B !important',
        letterSpacing: '0.0075em',
        marginBottom: '8px'
    },
    body1: {
        fontSize: '16px !important',
        lineHeight: '24px !important',
        fontStyle: 'normal',
        color: '#1E293B !important',
        letterSpacing: ' 0.0168em'
    },
    header: {
        marginBottom: '8px'
    },
    green: {
        color: 'green'
    },
    tokenInfo: {
        textAlign: 'center'
    },
    stakesLogo: {
        marginBottom: '20px'
    },
    overview: {
        backgroundColor: `${theme.palette.primary.dark} !important`,
        color: `${theme.palette.background.paper} !important`,
        padding: '20px',
        marginTop: '20px'
    },
    thumbnail: {
        width: '100%',
        height: '132px',
        borderRadius: '5px'
    },
    infoBox: {
        border: '1px solid #E2E8F0',
        borderRadius: '12px',
        padding: '15px',
        textAlign: 'center',
        backgroundColor: 'white'
    },
    block: {
        display: 'block'
    },
    perks: {
        whiteSpace: 'pre-line'
    },
    expired: {
        color: 'red !important',
        padding: '15px'
    }
}));

const LearnMore = ({token}: { token: IToken }) => {
    const classes = useStyles();

    return (
        <Box className={classes.infoBox}>
            <img src={token?.business?.logo?.file || LogoPlaceholder }
                 style={{height: '100px', width: '100px', objectFit: 'cover'}} alt='Owner'/>
            <br/><br/>
            <Box mb={1} textAlign="center">
                <Typography className={'wrapword'} color={'#475569'}>
                    {token?.business?.name}
                </Typography>
            </Box>
            <Box textAlign="left">
                <Typography className={'body2 wrapword'} color={'#475569'}>
                    {(token?.business?.description && token?.business?.description.length > 250) ? token?.business?.description.slice(0, 249) + '...' : token?.business?.description}
                </Typography>
            </Box>
            <br/>
            <Button variant='contained' className={'button w-100'}
                    component={RouterLink} to={`/business-page/${token?.business?.id}`}>
                <Typography fontWeight={700} className={'button-label'}>
                    Learn more
                </Typography>
            </Button>
        </Box>

    )
}

const TokenStats = ({token}: { token: IToken }) => {
    const classes = useStyles();

    let isOwnedToken = token?.parent_id && token?.owner_info?.expiration_readable && token?.profile?.renewal_price;

    return (
        <Box mt={1} className={classes.infoBox} sx={{ padding: '0'}}>
            <Box mt={1} textAlign="center">
                <Typography className={'wrapword'} color={'#475569'}>
                    {(isOwnedToken ? 'Your Membership' : 'Membership Details')}
                </Typography>
            </Box>
            <List dense={true}>

                {isOwnedToken && (
                <ListItem key={'token_id'}>
                    <ListItemIcon>
                            <CardMembershipIcon />
                    </ListItemIcon>
                    <ListItemText primary="Membership ID" secondary={token?.token_id_bc} />
                </ListItem>
                )}
                {isOwnedToken && (
                    <ListItem key={'expiration'}>
                    <ListItemIcon>
                            <AccessTimeIcon />
                    </ListItemIcon>
                    {/*<ListItemText sx={token?.owner_info?.status === 'EXPIRED' ? {color: 'red !important'} : {}} primary="Token Expiration" secondary={token?.owner_info?.expiration_readable} />*/}
                    <ListItemText secondaryTypographyProps={token?.owner_info?.status === 'EXPIRED' ? {sx: {color: 'red !important'}} : {}} primary="Membership Expiration" secondary={token?.owner_info?.expiration_readable} />
                </ListItem>
                )}
                {/*{isOwnedToken && (
                    <ListItem key={'owner_address'}>
                        <ListItemIcon>
                            <AccountBalanceWalletIcon />
                        </ListItemIcon>
                        <ListItemText primary="Owner Address" secondary={token?.owner_info?.address} />
                    </ListItem>
                )}*/}
                {/*{!isOwnedToken && (
                    <ListItem>
                        <ListItemIcon>
                            <ManageSearchIcon />
                        </ListItemIcon>
                        <ListItemText primary="Token Contract" secondary={token?.owner_info?.expiration_readable} />
                    </ListItem>
                )}*/}
                {!isOwnedToken && (
                    <ListItem key={'membership_duration'}>
                        <ListItemIcon>
                            <AccessTimeIcon />
                        </ListItemIcon>
                        <ListItemText primary="Membership Duration" secondary={+token?.profile?.expiration_duration === -1 ? "Never Expires" : token?.profile?.expiration_duration + " " + token?.profile?.expiration_duration_type} />
                    </ListItem>
                )}
                {/*{!isOwnedToken && (
                    <ListItem key={'memberships_per_addr'}>
                        <ListItemIcon>
                            <Grid3x3Icon />
                        </ListItemIcon>
                        <ListItemText primary="Memberships per Address" secondary={token?.profile?.keys_per} />
                    </ListItem>
                )}*/}

                <ListItem key={'renewal'}>
                    <ListItemIcon>
                            <PriceCheckIcon />
                    </ListItemIcon>
                    <ListItemText primary="Renewal Price" secondary={`${Number.parseInt(token.profile.renewal_price.toString()) === 0 ? "" : getCurrencySymbol(token?.profile?.currency)} ${Number.parseInt(token.profile.renewal_price.toString()) === 0 ? "FREE" : formatPrice(token.profile.renewal_price, token?.profile?.currency)}`} />
                </ListItem>

                {/*<ListItem key={'network'}>
                    <ListItemIcon>
                        <ManageSearchIcon />
                    </ListItemIcon>
                    <ListItemText primary="Blockchain Network" secondary={BLOCKCHAIN_NETWORK_ID[token?.profile?.blockchain_network_id]} />
                </ListItem>*/}

            </List>
        </Box>

    )
}

export const TokenOverview = ({token, children}: { token: IToken, children: React.ReactNode }) => {
    const classes = useStyles();
    const theme = useTheme();
    const smallScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const appSlice = useAppSelector(appSelector)

    const breadcrumbs = [
        <Link key="1" component={RouterLink} to="/public/tokens" color="inherit" href="/">
            Marketplace
        </Link>,
        <Typography key="3" color="inherit">
            {token?.profile?.title}
        </Typography>
    ];

    function secondsToDhms(seconds: number) {
        seconds = Number(seconds);
        var d = Math.floor(seconds / (3600 * 24));
        var h = Math.floor(seconds % (3600 * 24) / 3600);
        var m = Math.floor(seconds % 3600 / 60);
        var s = Math.floor(seconds % 60);

        var dDisplay = d > 0 ? d + (d == 1 ? " day " : " days ") : "";
        var hDisplay = h > 0 ? h + (h == 1 ? " hour, " : " hours ") : "";
        var mDisplay = m > 0 ? m + (m == 1 ? " minute " : " minutes ") : "";
        var sDisplay = s > 0 ? s + (s == 1 ? " second" : " seconds") : "";
        return dDisplay + hDisplay + mDisplay + sDisplay;
    }

    return (
        <>
            {!token || Object.keys(token).length === 0 ? (
                <TokenOverviewSkeleton/>
            ) : (
                <>
                    {/*<Box mb={6}>
                        <Breadcrumbs separator="›" aria-label="breadcrumb">
                            {breadcrumbs}
                        </Breadcrumbs>
                    </Box>*/}
                    <Grid container columnSpacing={4} sx={{flexDirection: {md: 'row', xs: 'column-reverse'}}}>
                        <Grid item md={9} xs={12}>
                            <Box mb={1}>
                                <Card sx={{padding: 4}}>
                                    <Box display="flex" justifyContent="space-between">
                                        <Box>
                                            <Typography className={'title2'} color={'#1E293B'}>
                                                {token?.profile?.title}
                                            </Typography>
                                            <Box display="flex" flexDirection={smallScreen ? 'column' : 'row'}
                                                 justifyContent="flex-start">
                                                <Box display="flex" alignItems="center" mr={2}>
                                                    <PersonOutlineIcon className="grey-color"/>
                                                    <Box ml={1}>
                                                        <Typography fontWeight={500} className={'body2'}
                                                                    color={'#64748B'}>
                                                            {token?.business?.name}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                                <Box display="flex" alignItems="center" mr={2}>
                                                    <PinDropIcon className="grey-color"/>
                                                    <Box ml={1}>
                                                        <Typography fontWeight={500} className={'body2'}
                                                                    color={'#64748B'}>
                                                            {token?.business?.city}, {token?.business?.state}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                                <Box display="flex" alignItems="center" mr={2}>
                                                    <MovieIcon className="grey-color"/>
                                                    <Box ml={1}>
                                                        <Typography fontWeight={500} className={'body2'}
                                                                    color={'#64748B'}>
                                                            {token?.profile?.category?.name}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </Box>
                                        </Box>
                                    </Box>
                                    {/*<Box my={4}>*/}
                                    <Box mt={4} mb={2}>
                                        <AppSlider images={token?.profile?.covers?.map(x => x.file)}/>
                                        {/*<Box my={4}>*/}
                                        <Box mt={4} mb={0}>
                                            <Typography className={'title3'} color={'#1E293B'} mb={2}>
                                                About {token?.profile?.title}
                                            </Typography>
                                            <Typography fontWeight={400} className={'body1 wrapword'} color={'#475569'}>
                                                {token?.profile?.description}
                                            </Typography>
                                        </Box>
                                        {token?.profile?.perks && Array.from(parseJSON(token?.profile?.perks))?.length > 0 && (
                                            <Box mt={4} >
                                                <Typography className={'title3'} color={'#1E293B'} sx={{ marginBottom: '27px' }}>
                                                    What's included?
                                                </Typography>
                                                {Array.from(parseJSON(token?.profile?.perks)).map((item: any, index) => (
                                                    <Accordion sx={{marginBottom: '15px'}} defaultExpanded={index === 0} key={index}>
                                                        <AccordionSummary
                                                            expandIcon={<ExpandMoreIcon style={{ color: '#fff' }} />}
                                                            aria-controls="panel1a-content"
                                                            id="panel1a-header"
                                                            key={index}
                                                            sx={{
                                                                background: '#041E42',
                                                                borderRadius: '5px 5px 0 0'

                                                            }}
                                                        >
                                                            <Typography className={'body1'} color={'#fff'}>
                                                                {item}
                                                            </Typography>
                                                        </AccordionSummary>
                                                        <AccordionDetails sx={{ paddingTop: '28px', background: '#fff', borderRadius: '0 0 4px 4px' }}>
                                                            <Typography className={classes.perks} style={{whiteSpace: 'pre-line'}}>
                                                               {`${(parseJSON(token?.profile?.perk_descriptions)[index])}`}
                                                            </Typography>
                                                        </AccordionDetails>
                                                    </Accordion>
                                                ))}
                                            </Box>
                                        )}
                                    </Box>
                                    <br />

                                </Card>


                            </Box>
                            <Box mt={2} mb={2} sx={{display: {md: 'none', xs: 'block'}}}>
                                <TokenStats token={token}/>
                            </Box>
                            <Box mb={2} mt={1} sx={{display: {md: 'none', xs: 'block'}}}>
                                <LearnMore token={token}/>
                            </Box>
                        </Grid>
                        <Grid item md={3} className={classes.tokenInfo}>
                            <Box display="flex" width="100%" sx={{flexDirection: {md: 'column', xs: 'row'}}}>
                                <Box sx={{width: {md: '100%', xs: '50%'}, minWidth: '100'}} mr={2}>
                                    <img height="150" width="150" style={{objectFit: 'contain'}} src={token?.profile?.art_image?.file}
                                         alt='Thumbnail'/>
                                </Box>
                                <Box textAlign="center">

                                    <Box my={2}>
                                        <Typography fontWeight={500} className={'caption'} color={'#64748B'}>
                                            {(token?.parent_id && token?.profile?.renewal_price) && (
                                                // TODO: Make this cleaner with a function
                                                <>Renewal Price</>
                                            )}
                                            {(token?.parent_id && !token?.profile?.renewal_price) || (!token?.parent_id) && (
                                                <>List Price</>
                                            )}
                                        </Typography>
                                    </Box>
                                    <Box display='flex' alignItems='center' justifyContent='center' my={1}>
                                        <Typography fontWeight={700} className={'subtitle1'}>
                                            {(token?.parent_id && token?.profile?.renewal_price) && (
                                                // TODO: Make this cleaner with a function
                                                Number.parseInt(token.profile.renewal_price.toString()) === 0 ? "" : getCurrencySymbol(token?.profile?.currency)
                                            )}
                                            {(token?.parent_id && !token?.profile?.renewal_price) || (!token?.parent_id) && (
                                                Number.parseInt(token.profile.price.toString()) === 0 ? "" : getCurrencySymbol(token?.profile?.currency)
                                            )}
                                        </Typography>
                                        <Typography fontWeight={700} className={'title2'}>
                                            {(token?.parent_id && token?.profile?.renewal_price) && (
                                                // formatPrice(token.profile.renewal_price, token?.profile?.currency)
                                                (Number.parseInt(token.profile.renewal_price.toString()) === 0 ? "FREE" : formatPrice(token?.profile?.renewal_price, token?.profile?.currency))
                                            )}
                                            {(token?.parent_id && !token?.profile?.renewal_price) || (!token?.parent_id) && (
                                                (Number.parseInt(token.profile.price.toString()) === 0 ? "FREE" : formatPrice(token?.profile?.price, token?.profile?.currency))
                                            )}


                                        </Typography>
                                    </Box>

                                    <Box display='flex' alignItems='center' justifyContent='center' textAlign={'center'} mb={1} mt={2}>
                                        {token?.profile?.copies && token?.profile?.copies === 0 && (
                                            <Typography className={'body2'} color='#475569'>
                                                You have no token.
                                            </Typography>
                                        )}
                                        {token?.profile?.copies && token?.profile?.copies > 0 && (
                                            <Typography className={'body2'} color='#475569' >
                                                {/*{((!token?.parent_id || !token?.is_open_for_sale) && token?.profile?.available_copies_count > 0) && (
                                                    <span> {token?.profile?.available_copies_count} out of {token?.profile?.copies ? token?.profile?.copies : 1} {token?.profile?.copies === 1 ? 'Membership' : 'Memberships'} available.</span>

                                                )}*/}
                                                {((!token?.parent_id || !token?.is_open_for_sale) && token?.profile?.available_copies_count == 0) && (
                                                    <span> No Memberships available.</span>

                                                )}
                                                {/*{(token?.parent_id && token?.is_open_for_sale) && (
                                                    <span> 1 out of 1 Membership available. </span>
                                                )}*/}
                                            </Typography>
                                        )}
                                    </Box>

                                </Box>
                            </Box>

                            {/* passing action from parent */}
                            {children}

                            <Box mb={3} sx={{display: {md: 'block', xs: 'none'}}}>
                                <TokenStats token={token}/>
                            </Box>
                            <Divider/>
                            <Box mt={3} sx={{display: {md: 'block', xs: 'none'}}}>
                                <LearnMore token={token}/>
                            </Box>
                        </Grid>
                    </Grid>
                </>
            )}

        </>
    )
}
